import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { MultiSelect, Select, Tooltip, useMantineTheme } from "@mantine/core";
import { Calendar as MantineCalendar } from "@mantine/dates";
import { FC, useMemo, useRef } from "react";
import { BsArrowLeft, BsArrowRight, BsChevronDown } from "react-icons/bs";
import { FiPlus, FiUser } from "react-icons/fi";
import { IoTodayOutline } from "react-icons/io5";
import { WiDayRainMix } from "react-icons/wi";
import {
  fieldsCompareFn,
  useFields,
} from "../../hooks/api/karl-farm/useFields";
import { useMachines } from "../../hooks/api/karl-farm/useMachines";
import { useUsers } from "../../hooks/api/karl-farm/useUsers";
import { useFirstDayOfWeek } from "../../hooks/useFirstDayOfWeek";
import { useLocale } from "../../hooks/useLocale";
import { useTranslation } from "../../i18n/useTranslation";
import { getCurrentDateDayStyleFunction } from "../../utils/getCurrentDateDayStyleFunction";
import { getFieldLabel } from "../../utils/getFieldLabel";
import { getFullName } from "../../utils/getFullName";
import { IconToggle } from "../IconToggle";
import {
  useDaysToDisplay,
  useFieldGroupsFilter,
  useFieldsFilter,
  useMachinesFilter,
  useTasksFilter,
  useUsersFilter,
} from "./storageHooks";
import { tasks, translateTask } from "../../hooks/api/karl-farm/constants";

const DateSelectButtonsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;

  font-size: 1.2rem;
`;

type CalendarPageHeaderProps = {
  startDate: Date;
  selectedDate?: Date;
  onMantineCalendarChange: (date: Date) => void;
  onPreviousClick: () => void;
  onTodayClick: () => void;
  onNextClick: () => void;
  showHeatMaps?: boolean;
  onShowHeatMapsToggle: () => void;
  colorTasksPerAssociatedUsers?: boolean;
  onColorTasksPerAssociatedUsersToggle: () => void;
  startCalendarOnSelectedDate?: boolean;
  onStartCalendarOnSelectedDateToggle: () => void;
  fieldGroupsData: string[];
  fieldsData: ReturnType<typeof useFields>["data"];
  usersData: ReturnType<typeof useUsers>["data"];
  machinesData: ReturnType<typeof useMachines>["data"];
  onAddTaskClick: () => void;
  taskSelected: boolean;
};
export const CalendarPageHeader: FC<CalendarPageHeaderProps> = ({
  startDate,
  selectedDate,
  onMantineCalendarChange,
  onPreviousClick,
  onTodayClick,
  onNextClick,
  showHeatMaps,
  onShowHeatMapsToggle,
  colorTasksPerAssociatedUsers,
  onColorTasksPerAssociatedUsersToggle,
  startCalendarOnSelectedDate,
  onStartCalendarOnSelectedDateToggle,
  fieldGroupsData,
  fieldsData,
  usersData,
  machinesData,
  onAddTaskClick,
  taskSelected,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslation();
  const firstDayOfWeek = useFirstDayOfWeek();
  const theme = useMantineTheme();

  const { value: rawFieldGroupsFilter, set: setFieldGroupsFilter } =
    useFieldGroupsFilter();
  const fieldGroupsFilter = useMemo(
    () => rawFieldGroupsFilter ?? [],
    [rawFieldGroupsFilter]
  );

  const { value: rawTasksFilter, set: setTasksFilter } = useTasksFilter();
  const tasksFilter = useMemo(() => rawTasksFilter ?? [], [rawTasksFilter]);

  const { value: rawFieldsFilter, set: setFieldsFilter } = useFieldsFilter();
  const fieldsFilter = useMemo(() => rawFieldsFilter ?? [], [rawFieldsFilter]);

  const { value: rawUsersFilter, set: setUsersFilter } = useUsersFilter();
  const usersFilter = useMemo(() => rawUsersFilter ?? [], [rawUsersFilter]);

  const { value: rawMachinesFilter, set: setMachinesFilter } =
    useMachinesFilter();
  const machinesFilter = useMemo(
    () => rawMachinesFilter ?? [],
    [rawMachinesFilter]
  );

  const hasActiveFilters =
    fieldGroupsFilter.length > 0 ||
    fieldsFilter.length > 0 ||
    tasksFilter.length > 0 ||
    usersFilter.length > 0 ||
    machinesFilter.length > 0;

  const fieldOptions = fieldsData?.data?.fields
    .filter((field) => {
      if (fieldGroupsFilter.length === 0) return true;
      if (!field.custom_attributes?.app_properties?.farm_name) return false;
      return fieldGroupsFilter.includes(
        field.custom_attributes?.app_properties?.farm_name
      );
    })
    .sort(fieldsCompareFn);

  const { value: daysToDisplay, set: setDaysToDisplay } = useDaysToDisplay();

  const filtersButtonRef = useRef<HTMLButtonElement>(null);
  // try again
  return (
    <Flex
      position="relative"
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"20px"}>
        <DateSelectButtonsRow>
          <Button
            variant="unstyled"
            display="flex"
            background="white"
            boxShadow="md"
            padding={4}
            onClick={onTodayClick}
          >
            {t("page.calendar.today")}
          </Button>
          <IconButton
            aria-label="Previous period"
            variant="unstyled"
            display="flex"
            background="white"
            boxShadow="md"
            onClick={onPreviousClick}
          >
            <BsArrowLeft size={20} />
          </IconButton>

          <IconButton
            aria-label="Next period"
            variant="unstyled"
            display="flex"
            background="white"
            boxShadow="md"
            onClick={onNextClick}
          >
            <BsArrowRight size={20} />
          </IconButton>
        </DateSelectButtonsRow>
        <Flex alignItems="center" gap="16px">
          <Heading as="h1" size="xl">
            <Popover>
              <PopoverTrigger>
                <Flex
                  alignItems="center"
                  gap="0.25em"
                  userSelect="none"
                  cursor="pointer"
                >
                  <Text textTransform={"capitalize"}>
                    {startDate.toLocaleDateString(locale, {
                      month: "long",
                    })}
                  </Text>
                  <Text as="span" fontWeight="normal" letterSpacing="tight">
                    {startDate.toLocaleDateString(locale, {
                      year: "numeric",
                    })}
                  </Text>
                  <Button
                    variant="unstyled"
                    display="flex"
                    background="white"
                    boxShadow="md"
                    padding={0}
                    size="xs"
                    marginLeft="0.25em"
                  >
                    <BsChevronDown size={16} />
                  </Button>
                </Flex>
              </PopoverTrigger>
              <PopoverContent width="auto">
                <PopoverArrow />
                <Flex padding="12px">
                  <MantineCalendar
                    locale={locale}
                    size="md"
                    value={selectedDate}
                    firstDayOfWeek={firstDayOfWeek}
                    onChange={onMantineCalendarChange}
                    dayStyle={getCurrentDateDayStyleFunction(theme)}
                  />
                </Flex>
              </PopoverContent>
            </Popover>
          </Heading>
        </Flex>
      </Flex>

      <Flex gap={"10px"} alignItems="center">
        {fieldGroupsData != null && (
          <Flex alignItems={"center"} gap={"1"} position={"relative"}>
            {/* {fieldGroupsFilter.length > 1 ? (
              <Button
                onClick={() => setFieldGroupsFilter([])}
                position={"absolute"}
                top={"0"}
                left={"0"}
                fontSize={"0.75rem"}
                height={"14px"}
                zIndex={"banner"}
                rounded={"sm"}
                colorScheme="red"
              >
                Clear filter
              </Button>
            ) : null} */}
            <FormControl>
              <MultiSelect
                searchable
                data={fieldGroupsData}
                value={fieldGroupsFilter}
                onChange={(value) => setFieldGroupsFilter(value)}
                placeholder={`${t(
                  "page.calendar.filters.placeholder.fieldGroups"
                )}`}
              />
            </FormControl>
          </Flex>
        )}
        <Select
          style={{ flex: "0 0 auto" }}
          sx={{ width: "100px" }}
          data={[
            { value: "1", label: t("page.calendar.days.1") },
            { value: "3", label: t("page.calendar.days.3") },
            { value: "7", label: t("page.calendar.days.7") },
          ]}
          value={daysToDisplay}
          onChange={(event) => {
            if (
              event === "1" ||
              event === "3" ||
              event === "7" ||
              event === "10"
            ) {
              setDaysToDisplay(event);
            } else {
              // Should never fire
              console.error("Invalid value for daysToDisplay", event);
            }
          }}
        />

        <Flex gap={"10px"} alignItems="center">
          {taskSelected && (
            <Tooltip
              label={t("page.calendar.showFieldConditions")}
              position="bottom"
              openDelay={300}
            >
              <IconToggle
                aria-label={t("page.calendar.showFieldConditions")}
                isHighlighted={showHeatMaps}
                onClick={onShowHeatMapsToggle}
              >
                <WiDayRainMix size={24} />
              </IconToggle>
            </Tooltip>
          )}

          {(daysToDisplay === "7" || daysToDisplay === "10") && (
            <Tooltip
              label={t("page.calendar.startCalendarOnSelectedDate.text")}
              position="bottom"
              openDelay={300}
            >
              <IconToggle
                aria-label={t("page.calendar.startCalendarOnSelectedDate.text")}
                isHighlighted={startCalendarOnSelectedDate}
                onClick={onStartCalendarOnSelectedDateToggle}
              >
                <IoTodayOutline size={22} />
              </IconToggle>
            </Tooltip>
          )}

          <Tooltip
            label={t("page.calendar.colorTasksPerAssociatedUsers.text")}
            position="bottom"
            openDelay={300}
          >
            <IconToggle
              aria-label={t("page.calendar.colorTasksPerAssociatedUsers.text")}
              isHighlighted={colorTasksPerAssociatedUsers}
              onClick={onColorTasksPerAssociatedUsersToggle}
            >
              <FiUser size={22} />
            </IconToggle>
          </Tooltip>
        </Flex>

        {/* Filters button */}
        <Popover>
          <PopoverTrigger>
            <Button
              style={{ flex: "0 0 auto" }}
              ref={filtersButtonRef}
              variant="unstyled"
              display="flex"
              background="white"
              boxShadow="md"
              padding={4}
              outline={hasActiveFilters ? "2px solid green" : undefined}
              outlineOffset="-2px"
            >
              {t("page.calendar.button.filters")}
            </Button>
          </PopoverTrigger>

          <PopoverContent>
            <PopoverArrow />

            <Flex direction="column" gap="16px" padding="16px">
              <FormControl>
                <FormLabel>{t("page.calendar.filters.label.tasks")}</FormLabel>
                <MultiSelect
                  searchable
                  value={tasksFilter}
                  onChange={(value) => setTasksFilter(value)}
                  data={tasks.map((task) => ({
                    value: task,
                    label: translateTask(t, task),
                  }))}
                />
              </FormControl>

              {fieldOptions != null && (
                <FormControl>
                  <FormLabel>
                    {t("page.calendar.filters.label.fields")}:
                  </FormLabel>
                  <MultiSelect
                    searchable
                    value={fieldsFilter}
                    onChange={(value) => setFieldsFilter(value)}
                    data={fieldOptions.map((field) => {
                      return {
                        value: field.id,
                        label: getFieldLabel(field),
                      };
                    })}
                  />
                </FormControl>
              )}

              {usersData?.data.users && (
                <FormControl>
                  <FormLabel>
                    {t("page.calendar.filters.label.users")}:
                  </FormLabel>
                  <MultiSelect
                    searchable
                    value={usersFilter}
                    onChange={(value) => setUsersFilter(value)}
                    data={
                      // It's okay to assume that the data is there, because we
                      // check for it above.
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      usersData.data.users!.map((user, i) => ({
                        value: user.Attributes.sub || i.toString(),
                        label:
                          getFullName(
                            user.Attributes.name,
                            user.Attributes.family_name
                          ) || "n/a",
                      }))
                    }
                  />
                </FormControl>
              )}

              {machinesData && (
                <FormControl>
                  <FormLabel>
                    {t("page.calendar.filters.label.machines")}:
                  </FormLabel>
                  <MultiSelect
                    searchable
                    value={machinesFilter}
                    onChange={(value) => setMachinesFilter(value)}
                    data={machinesData.data.machines.map((machine) => ({
                      value: machine.machine_id,
                      label: getFullName(machine.brand, machine.model) || "n/a",
                    }))}
                  />
                </FormControl>
              )}

              <Button
                variant="outline"
                disabled={!hasActiveFilters}
                onClick={() => {
                  setFieldsFilter([]);
                  setUsersFilter([]);
                  setMachinesFilter([]);
                  setTasksFilter([]);
                  filtersButtonRef.current?.click();
                }}
              >
                {t("page.calendar.filters.button.clearFilters")}
              </Button>
            </Flex>
          </PopoverContent>
        </Popover>

        {/* Add Task button */}
        <Tooltip
          label={t("page.calendar.iconButton.addTask.ariaLabel")}
          position="bottom"
          openDelay={300}
        >
          <IconButton
            aria-label={t("page.calendar.iconButton.addTask.ariaLabel")}
            variant={"solid"}
            boxShadow="md"
            onClick={onAddTaskClick}
          >
            <FiPlus size={24} />
          </IconButton>
        </Tooltip>
      </Flex>
    </Flex>
  );
};
